import PubSub from 'pubsub-js';
import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  LineBasicMaterial,
  LineSegments,
  Points,
  PointsMaterial,
  Scene,
  Vector3,
} from 'three';
import _ from 'underscore';

import { AsbuiltPoint } from '../../../../../typings/api/integrations/xsite/rest/v1/skymap-xsite-connection';
import { TokenService } from '../../../services/authentication/token.service';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { FileUpload } from '../../../services/network/file-upload';
import { WebApi } from '../../../services/web-api';
import { formattedDateTime } from '../../../utils/dateUtils';
import { isDefined } from '../../../utils/variables';
import { AssetManager } from '../../asset-manager';
import { CoordinateManager } from '../../coordinate-manager';
import { Design } from '../../elements/design';
import {
  generateDesignNodeFromAsbuiltPoints,
  serializeDesignNodeToGlb,
} from '../../elements/design/serialize';
import { GltfLoader } from '../../loader/gltf';
import { Point3d, toVector3 } from '../../utilities/math-helpers';

export type AsbuiltExportFormat = 'LandXml' | 'SkymapDesign';

const asbuiltPointMaterial = new PointsMaterial({
  transparent: false,
  color: new Color(0.1, 0.1, 0.9),
  opacity: 1.0,
  size: 8.0,
  sizeAttenuation: false,
});

const asbuiltLinematerial = new LineBasicMaterial({
  color: new Color(0.1, 0.1, 0.9),
});

const mapToPoint3d = (point: AsbuiltPoint): Point3d => {
  return {
    x: point.x ?? 0,
    y: point.y ?? 0,
    z: point.z ?? 0,
  };
};

export class AsbuiltGeometry extends Scene {
  static instance?: AsbuiltGeometry;

  private asbuiltPoints: AsbuiltPoint[] = [];
  private filteredAsbuiltPoints: AsbuiltPoint[] = [];

  private asbuiltPointsGeometry?: Points;
  private asbuiltLineGeometry?: LineSegments;

  constructor() {
    super();
    AsbuiltGeometry.instance = this;

    // When the coordinate projection is changed, rebuild the asbuilt data.
    PubSub.subscribe(
      CoordinateManager.getInstance().projectionUpdatedEvent,
      this.recreateAsbuiltGeometry.bind(this),
    );
  }

  /**
   *  Stores the new set of asbuilt points when they are changed.
   *  Then rebuild the geometry based on the new set.
   */
  setAsbuilt(asbuiltPoints: AsbuiltPoint[], filteredAsbuiltPoints: AsbuiltPoint[]) {
    this.asbuiltPoints = asbuiltPoints;
    this.filteredAsbuiltPoints = filteredAsbuiltPoints;

    this.recreateAsbuiltGeometry();
  }

  /**
   * Get the offset from the first point. This will be subtracted from each vertex
   * so that we can store it as local positions in the glb result.
   */
  private getGlbDataOffset() {
    return toVector3(mapToPoint3d(this.asbuiltPoints[0]), 0);
  }

  async exportAsbuiltAsSkymapDesign() {
    const name = `As-built ${formattedDateTime(new Date())}`;

    const offset = this.getGlbDataOffset();
    const result = generateDesignNodeFromAsbuiltPoints(this.asbuiltPoints, offset);
    const serializedGlb = serializeDesignNodeToGlb(
      result.node,
      result.attributes,
      {
        enabled: false,
      },
      {
        skipColors: true,
      },
    );

    const data = await GltfLoader.instance.loadGltf(Buffer.from(serializedGlb));

    const projection = CoordinateManager.getInstance().getProjectProjection();
    if (!projection) {
      throw new Error('Project projection is missing.');
    }

    // Get the WGS-position of the design based on the offset.
    const coords = CoordinateManager.getInstance().convertCoords(
      offset.y,
      offset.x,
      projection,
      'wgs84',
    );

    const geometry = new Design({
      name,
      visible: false,
      wgs84: {
        lat: coords.y,
        lng: coords.x,
      },
      MAS: 0,
    });

    geometry.loadGltf(data);

    // If save to S3 fails, remove geometry from asset list.
    // todo: Don't add geometry to asset list (addAsset) as a requirement to be save to S3.
    try {
      AssetManager.instance.addAsset(geometry);
      await geometry.saveGeometryToS3();
      AssetManager.instance.select(geometry);
    } catch (err) {
      AssetManager.instance.remove(geometry);
      throw err;
    }

    return name;
  }

  async exportAsbuiltAsLandXml() {
    if (!isDefined(this.asbuiltPointsGeometry?.geometry.attributes.position)) {
      throw new Error('No attribute position is set.');
    }

    const offset = this.getGlbDataOffset();
    const result = generateDesignNodeFromAsbuiltPoints(this.asbuiltPoints, offset);

    // Offset the group with the offset before serializing the result so the points will be correctly geo-referenced.
    result.node.group.position.copy(new Vector3(offset.y, offset.x, offset.z));

    const serializedGlb = serializeDesignNodeToGlb(result.node, result.attributes, {
      enabled: false,
    });

    const upload = new FileUpload({
      apiBaseUrl: WebApi.getUrl(),
      authToken: new TokenService().get(),
      file: new File([serializedGlb], 'geometry.glb'),
      concurrency: 1,
    });

    const uploadResult = await upload.start();

    const { data } = await SkyMapAxiosServiceFactory.instance.createUploadServiceV1().exportUpload({
      path: { id: uploadResult.id },
      query: { fromFormat: 'glb', toFormat: 'landxml' },
    });

    window.location.assign(data.url);
  }

  getAsbuiltPoint(index?: number) {
    return isDefined(index) && this.asbuiltPoints[index];
  }

  clear() {
    super.clear();

    PubSub.unsubscribe(this.recreateAsbuiltGeometry.bind(this));

    this.disposeOfGeometry();

    return this;
  }

  /**
   *  Release existing geometry and rebuilds it.
   *  Offsets the points based on the project origo.
   */
  private recreateAsbuiltGeometry() {
    this.clear();

    if (this.asbuiltPoints.length === 0) {
      return;
    }

    const activeOrigo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();
    const vertexBuffer = this.buildVertexBuffer(activeOrigo);
    this.asbuiltPointsGeometry = this.buildPointsGeometry(vertexBuffer);
    this.asbuiltLineGeometry = this.buildLinesGeometry(vertexBuffer);

    this.add(this.asbuiltPointsGeometry);
    this.add(this.asbuiltLineGeometry);
  }

  /**
   *  Builds the points geometry based of the asbuilt points.
   */
  private buildVertexBuffer(activeOrigo: Vector3) {
    const positionBuffer = new Float32BufferAttribute(
      _.flatten(
        this.asbuiltPoints.map((point) => [
          point.y! - activeOrigo.x,
          point.x! - activeOrigo.y,
          point.z! - activeOrigo.z,
        ]),
      ),
      3,
    );

    const asbuiltBufferGeometry = new BufferGeometry();
    asbuiltBufferGeometry.setAttribute('position', positionBuffer);

    return asbuiltBufferGeometry;
  }

  /**
   *  Builds the points geometry based of the asbuilt points.
   */
  private buildPointsGeometry(bufferGeometry: BufferGeometry) {
    return new Points(bufferGeometry, asbuiltPointMaterial);
  }

  /**
   *  Builds the line segments geometry  based on the line id of the points.
   */
  private buildLinesGeometry(bufferGeometry: BufferGeometry) {
    const lineIndices: Record<number, number[]> = this.extractLines(this.asbuiltPoints);
    const lineGeometryPoints: number[] = [];
    for (const lineIndexArray of Object.values(lineIndices)) {
      for (let i = 0; i < lineIndexArray.length - 1; i++) {
        lineGeometryPoints.push(lineIndexArray[i], lineIndexArray[i + 1]);
      }
    }

    const asbuiltBufferGeometry = bufferGeometry.clone();
    asbuiltBufferGeometry.setIndex(lineGeometryPoints);
    return new LineSegments(asbuiltBufferGeometry, asbuiltLinematerial);
  }

  private disposeOfGeometry() {
    if (isDefined(this.asbuiltPointsGeometry)) {
      this.asbuiltPointsGeometry?.geometry.dispose();
      this.asbuiltPointsGeometry = undefined;
    }

    if (isDefined(this.asbuiltLineGeometry)) {
      this.asbuiltLineGeometry?.geometry.dispose();
      this.asbuiltLineGeometry = undefined;
    }
  }

  /**
   *  Filters, groups and returns the points that are part of a line.
   */
  private extractLines(asbuiltPoints: AsbuiltPoint[]) {
    // Identify the line ids which has any of their points filtered.
    const filteredLineIds = new Set<number>(
      this.filteredAsbuiltPoints
        .filter((point) => isDefined(point.lineId))
        .map((point) => point.lineId!),
    );

    // Filter lines that is missing any point
    const lineIndices = asbuiltPoints.reduce(
      (
        indices: Record<number, number[]>,
        point: AsbuiltPoint,
        key: number,
      ): Record<number, number[]> => {
        if (isDefined(point.lineId) && !filteredLineIds.has(point.lineId)) {
          indices[point.lineId] = indices[point.lineId] ?? [];
          indices[point.lineId].push(key);
        }

        return indices;
      },
      {},
    );

    return lineIndices;
  }
}
